import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import GetAppIcon from "@mui/icons-material/GetApp";
import { billingTriggerActions } from "../../../backend/store/actions/creators/billingActions";
import { billingAPI } from "../../../backend/api/api";
import { StateType } from "../../../backend/store";
import { useTranslation } from "../../../hooks/useTranslation";

const ACCEPTED_EXTENSIONS = [".csv", ".xlsx", ".xls", ".zip", ".pdf", ".doc", ".docx"];
const MAX_TOTAL_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB
const MAX_FILE_COUNT = 5;

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

interface ManagedUploadAreaProps {
  adminMessage?: string | null;
}

export const ManagedUploadArea: React.FC<ManagedUploadAreaProps> = ({ adminMessage }) => {
  const dispatch = useDispatch();
  const { managedUploadRequested, managedUploadFileUploaded, managedUploadStatus } = useSelector(
    (state: StateType) => state.billingStore
  );
  const { p } = useTranslation("upload_instructions");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(
    sessionStorage.getItem("managedUploadHintDismissed") === "true"
  );

  if (!managedUploadRequested) return null;

  if (managedUploadStatus === "completed") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 2,
          border: "1px solid",
          borderColor: "success.main",
          borderRadius: 2,
          bgcolor: "success.50",
          mb: 2,
        }}
      >
        <CheckCircleIcon color="success" />
        <Typography variant="body2" color="success.dark">
          Your data has been imported successfully.
        </Typography>
      </Box>
    );
  }

  if (managedUploadStatus === "in_progress") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          border: "1px solid",
          borderColor: "info.main",
          borderRadius: 2,
          bgcolor: "info.50",
          mb: 2,
        }}
      >
        <CircularProgress size={20} color="info" />
        <Typography variant="body2" color="info.dark">
          {p("managed_processing")}
        </Typography>
      </Box>
    );
  }

  // Show upload area for pending, reupload_requested, or when file not yet uploaded
  const showUploadArea =
    !managedUploadFileUploaded ||
    managedUploadStatus === "reupload_requested";

  if (!showUploadArea) return null;

  const validateFiles = (files: File[]): string | null => {
    for (const file of files) {
      const ext = "." + file.name.split(".").pop()?.toLowerCase();
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        return p("managed_err_unsupported", { name: file.name, exts: ACCEPTED_EXTENSIONS.join(", ") });
      }
    }
    return null;
  };

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;
    const newFiles = Array.from(incoming);

    const combined = [...selectedFiles, ...newFiles];

    // Deduplicate by name+size
    const deduped = combined.filter(
      (file, index, arr) =>
        arr.findIndex((f) => f.name === file.name && f.size === file.size) === index
    );

    if (deduped.length > MAX_FILE_COUNT) {
      setValidationError(p("managed_err_max_files", { max: MAX_FILE_COUNT }));
      return;
    }

    const extError = validateFiles(newFiles);
    if (extError) {
      setValidationError(extError);
      return;
    }

    const totalSize = deduped.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > MAX_TOTAL_SIZE_BYTES) {
      setValidationError(
        p("managed_err_total_size", { size: formatBytes(totalSize) })
      );
      return;
    }

    setValidationError(null);
    setSelectedFiles(deduped);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleClick = () => fileInputRef.current?.click();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files);
    // Reset so the same file can be re-selected
    e.target.value = "";
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setValidationError(null);
  };

  const handleUploadAll = () => {
    if (selectedFiles.length === 0) return;
    setIsUploading(true);
    dispatch(billingTriggerActions.uploadManagedFile(selectedFiles));
    // Reset local state — the saga will update the store and re-render
    setSelectedFiles([]);
    setIsUploading(false);
  };

  const handleDownloadTemplate = async () => {
    const response = await billingAPI.downloadImportTemplate();
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = "connectyid-template.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

  return (
    <Box sx={{ mb: 2 }}>
      {adminMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {adminMessage}
        </Alert>
      )}

      {managedUploadStatus === "reupload_requested" && !adminMessage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Our team has requested that you re-upload your files.
        </Alert>
      )}

      <Collapse in={!hintDismissed && managedUploadStatus === "pending"}>
        <Alert
          severity="info"
          onClose={() => {
            setHintDismissed(true);
            sessionStorage.setItem("managedUploadHintDismissed", "true");
          }}
          sx={{ mb: 2 }}
        >
          <AlertTitle>{p("managed_hint_title")}</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {p("managed_hint_body")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
            {p("managed_key_fields")}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={handleDownloadTemplate}
          >
            {p("download_template")}
          </Button>
        </Alert>
      </Collapse>

      {/* Drop zone */}
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
        sx={{
          border: "2px dashed",
          borderColor: dragOver ? "primary.main" : "divider",
          borderRadius: 2,
          p: 4,
          textAlign: "center",
          cursor: "pointer",
          bgcolor: dragOver ? "action.hover" : "background.paper",
          transition: "border-color 0.2s, background-color 0.2s",
          "&:hover": {
            borderColor: "primary.light",
            bgcolor: "action.hover",
          },
        }}
      >
        <UploadFileIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
        <Typography variant="subtitle2" gutterBottom>
          {p("managed_drop_zone")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {p("managed_accepted")}
        </Typography>
      </Box>

      {validationError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
          {validationError}
        </Typography>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".csv,.xlsx,.xls,.zip,.pdf,.doc,.docx"
        style={{ display: "none" }}
        onChange={handleInputChange}
      />

      {/* Selected file list */}
      {selectedFiles.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
            {p("managed_files_selected", { count: selectedFiles.length, size: formatBytes(totalSize) })}
          </Typography>
          <List dense disablePadding>
            {selectedFiles.map((file, index) => (
              <ListItem
                key={`${file.name}-${file.size}-${index}`}
                disableGutters
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  px: 1.5,
                  mb: 0.5,
                }}
              >
                <ListItemText
                  primary={file.name}
                  secondary={formatBytes(file.size)}
                  primaryTypographyProps={{ variant: "body2", noWrap: true }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile(index);
                    }}
                    aria-label={`Remove ${file.name}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button variant="outlined" size="small" onClick={handleClick}>
          {p("managed_add_files")}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleUploadAll}
          disabled={selectedFiles.length === 0 || isUploading}
          startIcon={isUploading ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {isUploading ? p("managed_uploading") : p("managed_upload_all")}
        </Button>
      </Box>
    </Box>
  );
};
